import { useEffect, useMemo, useState, useCallback } from "react";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { getAllGrupos } from "../../../services/grupo.service";
import { getAllIdiomas } from "../../../services/idioma.service";
import { getAllRegistros } from "../../../services/registro.service";
import { GrupoList } from "../../../modules/grupo/GrupoList";
import { TITULO_GRUPO_LIST } from "../../../utils/constantes";

export const GrupoFiltrarPorNota = () => {
  const [idiomas, setIdiomas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [filtroIdioma, setFiltroIdioma] = useState("");
  const [filtroNotas, setFiltroNotas] = useState("dividir"); // "dividir", "todos", "sinNotas", "conNotas"
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---- helpers para horarios (filtra soft delete / inhabilitados) ----
  const limpiarHorarios = useCallback((arr = []) => {
    const norm = (h) => ({
      id: h.idHorarioGrupo ?? h.idHorario ?? `${h?.dia?.nombre}-${h?.horaInicio}-${h?.horaFin}`,
      horaInicio: h.horaInicio ?? h.horario?.horaInicio ?? "?",
      horaFin: h.horaFin ?? h.horario?.horaFin ?? "?",
      dia: h.dia ?? h.horario?.dia ?? { nombre: "?" },
      eliminado: h.eliminado,
      activo: h.activo,
      estado: h.estado,
      fechaBaja: h.fechaBaja,
      habilitado: h.habilitado,
      inhabilitado: h.inhabilitado,
    });

    const visible = (h) => {
      if (h.eliminado) return false;
      if (h.fechaBaja) return false;
      if (h.inhabilitado) return false;
      if (h.habilitado === false) return false;
      if (h.activo === false) return false;
      if (h.estado != null) {
        const e = String(h.estado).toUpperCase();
        if (["INHABILITADO", "ELIMINADO", "DESHABILITADO", "0", "FALSE", "F"].includes(e)) return false;
      }
      return true;
    };

    const únicos = new Map();
    for (const raw of arr) {
      const h = norm(raw);
      if (visible(h)) únicos.set(h.id, h);
    }
    return Array.from(únicos.values());
  }, []);

  // Normalizar texto de búsqueda
  const normalizeText = useCallback((text = "") => {
    return text
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }, []);

  // Cargar datos con manejo de errores + limpieza de horarios
  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      const [gruposRes, idiomasRes, registrosRes] = await Promise.all([
        getAllGrupos(), 
        getAllIdiomas(),
        getAllRegistros()
      ]);

      // Agrupar registros por idGrupo (solo habilitados)
      const registrosPorGrupo = {};
      (registrosRes || [])
        .filter(r => r.habilitado === true)
        .forEach(registro => {
          if (!registrosPorGrupo[registro.idGrupo]) {
            registrosPorGrupo[registro.idGrupo] = [];
          }
          registrosPorGrupo[registro.idGrupo].push(registro);
        });

      // Verificar si todos los estudiantes de un grupo tienen notas completas
      const verificarNotasCompletas = (idGrupo) => {
        const registros = registrosPorGrupo[idGrupo] || [];
        if (registros.length === 0) return false; // No hay estudiantes, considerar sin notas

        // Función para verificar si un estudiante tiene notas realmente ingresadas Y está en estado final
        const tieneNotasIngresadas = (registro) => {
          // Convertir valores a números de forma segura
          const convertirNumero = (valor) => {
            if (valor === null || valor === undefined || valor === '') return null;
            const num = Number(valor);
            return isNaN(num) ? null : num;
          };

          // Verificar el estado del registro - debe estar en un estado final (no en progreso)
          const estado = String(registro.estado || '').trim().toLowerCase();
          const estadosFinales = ['aprobado', 'reprobado', 'culminado'];
          const estaEnEstadoFinal = estadosFinales.includes(estado);

          // Si no está en estado final, no tiene notas completas (aunque tenga notas parciales)
          if (!estaEnEstadoFinal) {
            return false;
          }

          const notaFinal = convertirNumero(registro.notaFinal);
          const examenBloque2 = convertirNumero(registro.examenBloque2);
          const promedioBloque2 = convertirNumero(registro.promedioBloque2);

          // Si tiene notaFinal válida (no null, no -1, >= 0), tiene notas completas
          if (notaFinal !== null && notaFinal !== -1 && notaFinal >= 0) {
            return true;
          }

          // Si tiene examenBloque2 válido (no null, no -1, >= 0) Y promedioBloque2 válido, tiene notas
          if (examenBloque2 !== null && examenBloque2 !== -1 && examenBloque2 >= 0 &&
              promedioBloque2 !== null && promedioBloque2 >= 0) {
            return true;
          }

          // Si no cumple ninguna condición, no tiene notas válidas
          // Nota: No consideramos solo promedioBloque2 porque podría ser un valor parcial
          // Requerimos notaFinal O (examenBloque2 Y promedioBloque2) para considerar notas completas
          return false;
        };

        // Todos los estudiantes deben tener notas ingresadas Y estar en estado final
        const todosTienenNotas = registros.every(tieneNotasIngresadas);
        
        return todosTienenNotas;
      };

      // Aplico limpieza a los horarios de cada grupo y agrego el estado de notas
      const gruposLimpios = (gruposRes || []).map((g) => ({
        ...g,
        horarios: limpiarHorarios(g.horarios || []),
        tieneNotasCompletas: verificarNotasCompletas(g.idGrupo),
        cantidadEstudiantes: (registrosPorGrupo[g.idGrupo] || []).length,
      }));

      setGrupos(gruposLimpios);
      setIdiomas(idiomasRes);
      setError(null);
    } catch (err) {
      console.error("Error cargando datos:", err);
      setError("Error al cargar los grupos. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  }, [limpiarHorarios]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Re-fetch al volver foco a la pestaña (útil al cerrar edición y volver)
  useEffect(() => {
    const onFocus = () => cargarDatos();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [cargarDatos]);

  // Procesar grupos para búsqueda
  const gruposProcesados = useMemo(() => {
    return grupos.map((grupo) => ({
      ...grupo,
      idIdioma: grupo.modulo?.idioma?.idIdioma || grupo.modulo?.idIdioma,
      searchString: `${grupo.nombre} ${grupo.nombreAntGrupo || ""} ${grupo.modulo?.nombre || ""} ${grupo.modulo?.sigla || ""}`,
    }));
  }, [grupos]);

  // Filtrar grupos según criterios
  const gruposFiltrados = useMemo(() => {
    if (!filtroIdioma && !busqueda) return gruposProcesados;

    const terminoBusqueda = busqueda ? normalizeText(busqueda) : null;

    return gruposProcesados.filter((grupo) => {
      if (filtroIdioma && grupo.idIdioma !== filtroIdioma) return false;
      if (terminoBusqueda) return normalizeText(grupo.searchString).includes(terminoBusqueda);
      return true;
    });
  }, [gruposProcesados, filtroIdioma, busqueda, normalizeText]);

  // Separar grupos con y sin notas completas
  const gruposSinNotas = useMemo(() => {
    return gruposFiltrados.filter(grupo => !grupo.tieneNotasCompletas);
  }, [gruposFiltrados]);

  const gruposConNotas = useMemo(() => {
    return gruposFiltrados.filter(grupo => grupo.tieneNotasCompletas);
  }, [gruposFiltrados]);

  if (loading) {
    return (
      <ContenedorPageTemplate titulo="Filtrar Grupos por Notas">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-lg">Cargando grupos...</span>
        </div>
      </ContenedorPageTemplate>
    );
  }

  if (error) {
    return (
      <ContenedorPageTemplate titulo="Filtrar Grupos por Notas">
        <div className="flex flex-col items-center justify-center h-64 p-4 bg-red-50 rounded-lg">
          <p className="text-red-600 font-medium text-lg mb-3">{error}</p>
          <button
            onClick={cargarDatos}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </ContenedorPageTemplate>
    );
  }

  return (
    <ContenedorPageTemplate titulo="Filtrar Grupos por Notas">
      {/* Barra de filtros mejorada */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4">
          {/* Filtro por idioma */}
          <div className="relative flex-1 lg:flex-none lg:w-64">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Idioma
            </label>
            <select
              value={filtroIdioma}
              onChange={(e) => setFiltroIdioma(e.target.value)}
              className="w-full appearance-none bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium py-2.5 px-4 pr-10 rounded-lg shadow-md hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition-all"
            >
              <option value="" className="bg-white text-gray-800">
                🌐 Todos los idiomas
              </option>
              {idiomas.map(({ idIdioma, nombre }) => (
                <option key={idIdioma} value={idIdioma} className="bg-white text-gray-800">
                  {nombre}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 pt-6">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Filtro por estado de notas */}
          <div className="relative flex-1 lg:flex-none lg:w-64">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Estado de Notas
            </label>
            <select
              value={filtroNotas}
              onChange={(e) => setFiltroNotas(e.target.value)}
              className="w-full appearance-none bg-gradient-to-r from-purple-500 to-purple-600 text-white font-medium py-2.5 px-4 pr-10 rounded-lg shadow-md hover:from-purple-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 transition-all"
            >
              <option value="dividir" className="bg-white text-gray-800">
                📊 Dividir por notas
              </option>
              <option value="todos" className="bg-white text-gray-800">
                📚 Todos juntos
              </option>
              <option value="sinNotas" className="bg-white text-gray-800">
                ⚠️ Solo sin notas
              </option>
              <option value="conNotas" className="bg-white text-gray-800">
                ✅ Solo con notas
              </option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 pt-6">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Búsqueda */}
          <div className="flex-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Búsqueda
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar por nombre, módulo, sigla…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full border-2 border-gray-300 rounded-lg py-2.5 px-4 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500 transition-all"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pt-6">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Estadísticas mejoradas con diseño moderno */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border border-blue-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium mb-1">Total de grupos</p>
              <p className="text-4xl font-bold">{gruposFiltrados.length}</p>
              <p className="text-blue-200 text-xs mt-1">Filtrados actualmente</p>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-sm p-4 rounded-full">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 via-red-500 to-red-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border border-orange-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium mb-1">Pendientes</p>
              <p className="text-4xl font-bold">{gruposSinNotas.length}</p>
              <p className="text-orange-200 text-xs mt-1">
                {gruposFiltrados.length > 0 
                  ? `${Math.round((gruposSinNotas.length / gruposFiltrados.length) * 100)}% del total`
                  : "Sin grupos"}
              </p>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-sm p-4 rounded-full">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 via-emerald-500 to-green-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border border-green-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium mb-1">Completados</p>
              <p className="text-4xl font-bold">{gruposConNotas.length}</p>
              <p className="text-green-200 text-xs mt-1">
                {gruposFiltrados.length > 0 
                  ? `${Math.round((gruposConNotas.length / gruposFiltrados.length) * 100)}% del total`
                  : "Sin grupos"}
              </p>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-sm p-4 rounded-full">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Vista según filtro seleccionado */}
      {filtroNotas === "dividir" && (
        <div className="space-y-6">
          {/* Grupos SIN notas completas */}
          {gruposSinNotas.length > 0 && (
            <div className="bg-white rounded-xl shadow-xl border-l-4 border-orange-500 overflow-hidden">
              <div className="bg-gradient-to-r from-orange-50 via-red-50 to-orange-50 px-6 py-5 border-b border-orange-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-orange-500 to-red-500 p-3 rounded-full shadow-md">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-orange-800">
                        Grupos Pendientes
                      </h3>
                      <p className="text-orange-600 text-sm font-medium">
                        {gruposSinNotas.length} {gruposSinNotas.length === 1 ? 'grupo requiere' : 'grupos requieren'} completar notas
                      </p>
                    </div>
                  </div>
                  <div className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full font-bold text-lg">
                    {gruposSinNotas.length}
                  </div>
                </div>
              </div>
              <div className="p-6 bg-gray-50">
                <GrupoList grupos={gruposSinNotas} />
              </div>
            </div>
          )}

          {/* Grupos CON notas completas */}
          {gruposConNotas.length > 0 && (
            <div className="bg-white rounded-xl shadow-xl border-l-4 border-green-500 overflow-hidden">
              <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-green-50 px-6 py-5 border-b border-green-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-green-500 to-emerald-500 p-3 rounded-full shadow-md">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-green-800">
                        Grupos Completados
                      </h3>
                      <p className="text-green-600 text-sm font-medium">
                        {gruposConNotas.length} {gruposConNotas.length === 1 ? 'grupo tiene' : 'grupos tienen'} todas las notas registradas
                      </p>
                    </div>
                  </div>
                  <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-bold text-lg">
                    {gruposConNotas.length}
                  </div>
                </div>
              </div>
              <div className="p-6 bg-gray-50">
                <GrupoList grupos={gruposConNotas} />
              </div>
            </div>
          )}

          {/* Mensaje cuando no hay grupos */}
          {gruposFiltrados.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <p className="text-lg text-gray-600 font-medium">No se encontraron grupos</p>
              <p className="text-gray-500">Intenta ajustar los filtros de búsqueda</p>
            </div>
          )}
        </div>
      )}

      {/* Vista TODOS JUNTOS */}
      {filtroNotas === "todos" && (
        <GrupoList grupos={gruposFiltrados} />
      )}

      {/* Vista SOLO SIN NOTAS */}
      {filtroNotas === "sinNotas" && (
        <div>
          {gruposSinNotas.length > 0 ? (
            <div className="bg-white rounded-xl shadow-xl border-l-4 border-orange-500 overflow-hidden">
              <div className="bg-gradient-to-r from-orange-50 via-red-50 to-orange-50 px-6 py-5 border-b border-orange-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-orange-500 to-red-500 p-3 rounded-full shadow-md">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-orange-800">
                        Grupos Pendientes
                      </h3>
                      <p className="text-orange-600 text-sm font-medium">
                        Requieren completar las notas de todos los estudiantes
                      </p>
                    </div>
                  </div>
                  <div className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full font-bold text-lg">
                    {gruposSinNotas.length}
                  </div>
                </div>
              </div>
              <div className="p-6 bg-gray-50">
                <GrupoList grupos={gruposSinNotas} />
              </div>
            </div>
          ) : (
            <div className="text-center py-16 bg-gradient-to-br from-green-50 via-emerald-50 to-green-50 rounded-xl border-2 border-green-200 shadow-lg">
              <div className="text-green-500 mb-6">
                <svg className="w-20 h-20 mx-auto animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
              <p className="text-2xl text-green-700 font-bold mb-3">¡Excelente trabajo!</p>
              <p className="text-green-600 text-lg">Todos los grupos tienen notas completas</p>
            </div>
          )}
        </div>
      )}

      {/* Vista SOLO CON NOTAS */}
      {filtroNotas === "conNotas" && (
        <div>
          {gruposConNotas.length > 0 ? (
            <div className="bg-white rounded-xl shadow-xl border-l-4 border-green-500 overflow-hidden">
              <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-green-50 px-6 py-5 border-b border-green-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-green-500 to-emerald-500 p-3 rounded-full shadow-md">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-green-800">
                        Grupos Completados
                      </h3>
                      <p className="text-green-600 text-sm font-medium">
                        Todos los estudiantes tienen notas registradas
                      </p>
                    </div>
                  </div>
                  <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-bold text-lg">
                    {gruposConNotas.length}
                  </div>
                </div>
              </div>
              <div className="p-6 bg-gray-50">
                <GrupoList grupos={gruposConNotas} />
              </div>
            </div>
          ) : (
            <div className="text-center py-16 bg-gradient-to-br from-orange-50 via-red-50 to-orange-50 rounded-xl border-2 border-orange-200 shadow-lg">
              <div className="text-orange-500 mb-6">
                <svg className="w-20 h-20 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
              </div>
              <p className="text-2xl text-orange-700 font-bold mb-3">Aún no hay grupos completados</p>
              <p className="text-orange-600 text-lg">Los grupos aparecerán aquí cuando tengan todas las notas registradas</p>
            </div>
          )}
        </div>
      )}
    </ContenedorPageTemplate>
  );
};
export default GrupoFiltrarPorNota;
